// One face per served form. Every figure drawn here is a field of the served body, formatted, and
// every word for an engine code is the served vocabulary's. Grouping served rows under their
// account type's heading is presentation; adding any two figures is not, and nothing here does.
// The rows already arrive in the statement's face order (BM-BE7).

import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Icon, cn } from '@givmo/design-system';
import type {
  BooksActivities,
  BooksActivityRow,
  BooksBalanceSheet,
  BooksClassColumnTotals,
  BooksDimensionMember,
  BooksFinancialPosition,
  BooksForm990View,
  BooksFunctionalExpenses,
  BooksIncomeStatement,
  BooksPositionRow,
  BooksReserveNote,
  BooksStatementBody,
  BooksStatementSection,
  BooksTrialBalance,
} from './books-wire';
import { Money, StatementTable, type FaceRow } from './books-ui';
import {
  count,
  dateShort,
  decimalMoney,
  listWords,
  money,
  moneyOrDash,
  periodName,
} from './books-format';
import type { BooksWords } from './books-words';

const DRAWN_FORMS = new Set([
  'financial_position',
  'activities',
  'functional_expenses',
  'form990_view',
  'trial_balance',
  'income_statement',
  'balance_sheet',
]);

/** Whether this build draws a served form; one it does not is said plainly, never guessed at. */
export function isDrawnForm(body: { form: string }): body is BooksStatementBody {
  return DRAWN_FORMS.has(body.form);
}

export function StatementFace({ body, words }: { body: BooksStatementBody; words: BooksWords }) {
  switch (body.form) {
    case 'financial_position':
      return <PositionFace body={body} words={words} />;
    case 'activities':
      return <ActivitiesFace body={body} words={words} />;
    case 'functional_expenses':
      return <FunctionalExpensesFace body={body} words={words} />;
    case 'form990_view':
      return <Form990Face body={body} words={words} />;
    case 'trial_balance':
      return <TrialBalanceFace body={body} words={words} />;
    case 'income_statement':
      return <IncomeStatementFace body={body} words={words} />;
    case 'balance_sheet':
      return <BalanceSheetFace body={body} words={words} />;
  }
}

const cellOf = (cents: number) => <Money cents={cents} />;
const dashOf = (cents: number) => (
  <span className={cents === 0 ? 'text-text-tertiary' : undefined}>{moneyOrDash(cents)}</span>
);

/**
 * An axis member in words: on the axis the books serve as their functional one, the category's own
 * words; any other axis's member as served.
 */
export function memberWords(words: BooksWords, axisId: string, member: string): string {
  return axisId === words.functionalAxisId ? words.category(member) : member;
}

/** Accounts with nothing on them fold away on an account-level face; one click shows them. */
function ZeroToggle({
  hidden,
  shown,
  onToggle,
  what,
  narrow = false,
}: {
  hidden: number;
  shown: boolean;
  onToggle: () => void;
  what: 'no balance' | 'no activity';
  /** Aligns with a narrow face's right edge. */
  narrow?: boolean;
}) {
  if (hidden === 0) return null;
  return (
    <div className={cn('flex justify-end px-16 pt-4', narrow && 'max-w-[760px]')}>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={shown}
        className="font-sans text-s12 font-w600 tracking-givmo text-brand-600 hover:underline"
        data-testid="books-zero-toggle"
      >
        {shown
          ? `Hide accounts with ${what}`
          : `Show ${hidden} ${hidden === 1 ? 'account' : 'accounts'} with ${what}`}
      </button>
    </div>
  );
}

function Unbalanced({ testId, children }: { testId: string; children: ReactNode }) {
  return (
    <p
      role="alert"
      className="px-16 font-sans text-s13 tracking-givmo text-danger-700"
      data-testid={testId}
    >
      {children}
    </p>
  );
}

// ── A statement of financial position (the board pack's) ─────────────────────

const PROCESSOR_RESERVE = 'processor_reserve';
const SUSPENSE = 'suspense';

function PositionFace({ body, words }: { body: BooksFinancialPosition; words: BooksWords }) {
  const [openNote, setOpenNote] = useState<string | null>(null);
  const rowsOf = (type: string) => body.rows.filter((row) => row.account_type === type);
  // A net-asset suspense balance is one of the balances its class's accounts hold: the engine names
  // the class each net-asset row folds into (`net_asset_class`, PT-BK25), and counts the row in that
  // class's served figures. So the make-up places it under its served class, inside "Held in
  // net-asset accounts" as an "of which" line — a part of that served figure, never a figure to add
  // beside it. The make-up is one click away (D-BM-17 (3)); while it is closed, a suspense balance
  // is still never hidden (D-BM-16): one muted cue under its class's row says what the class figure
  // includes, its figure inside the sentence and none in the amount column, so it can't read as an
  // addend. Opened, the "of which" line takes its place.
  const suspenseOf = (netAssetClass: string) =>
    rowsOf('net_asset').filter(
      (row) =>
        row.section === SUSPENSE && row.amount_cents !== 0 && row.net_asset_class === netAssetClass,
    );
  const [makeup, setMakeup] = useState(false);

  const lineOf = (row: BooksPositionRow): FaceRow[] => {
    const label = row.label ?? words.section(row.section, row.account_type);
    const reserve = row.section === PROCESSOR_RESERVE;
    const noteKey = row.account_codes.join('-');
    const lines: FaceRow[] = [
      {
        kind: 'line',
        key: `${row.account_type}-${row.section}-${noteKey}`,
        indent: 1,
        label,
        cells: [dashOf(row.amount_cents)],
        note: reserve ? (
          <span className="inline-flex flex-wrap items-center gap-8">
            <span className="font-sans text-s12 tracking-givmo text-text-tertiary">
              {[
                'Restricted',
                row.classification,
                row.excluded_from_liquidity ? 'not available cash' : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
            {row.notes.length > 0 ? (
              <button
                type="button"
                onClick={() => setOpenNote(openNote === noteKey ? null : noteKey)}
                aria-expanded={openNote === noteKey}
                className="inline-flex items-center gap-2 font-sans text-s12 font-w600 tracking-givmo text-brand-600 hover:underline"
                data-testid="books-reserve-note-toggle"
              >
                <Icon
                  icon={openNote === noteKey ? ChevronDown : ChevronRight}
                  size={12}
                  aria-hidden
                />
                Note
              </button>
            ) : null}
          </span>
        ) : undefined,
      },
    ];
    // The reserve's disclosure opens directly beneath its own line.
    if (reserve && openNote === noteKey) {
      lines.push({
        kind: 'detail',
        key: `${noteKey}-notes`,
        content: <ReserveNotes notes={row.notes} words={words} />,
      });
    }
    return lines;
  };

  // Each class's served total on the face; what makes it up (what its accounts hold, and the change
  // no closing entry has carried, split at the fiscal-year boundary) is one click away, all served.
  const classRows = body.net_assets.flatMap((netAssetClass): FaceRow[] => [
    {
      kind: 'line',
      key: `na-${netAssetClass.net_asset_class}`,
      indent: 1,
      label: words.netAssetClass(netAssetClass.net_asset_class),
      cells: [cellOf(netAssetClass.total_cents)],
    },
    ...(makeup
      ? ([
          {
            kind: 'line',
            key: `na-${netAssetClass.net_asset_class}-booked`,
            indent: 2,
            muted: true,
            label: 'Held in net-asset accounts',
            cells: [dashOf(netAssetClass.booked_cents)],
          },
          ...suspenseOf(netAssetClass.net_asset_class).map(
            (row): FaceRow => ({
              kind: 'line',
              key: `na-${netAssetClass.net_asset_class}-${row.section}`,
              indent: 3,
              muted: true,
              label: (
                <span data-testid="books-net-assets-of-which">
                  of which {words.section(row.section, row.account_type).toLowerCase()}
                </span>
              ),
              cells: [dashOf(row.amount_cents)],
            }),
          ),
          {
            kind: 'line',
            key: `na-${netAssetClass.net_asset_class}-fytd`,
            indent: 2,
            muted: true,
            label: 'Change this fiscal year, not yet closed',
            cells: [dashOf(netAssetClass.fiscal_year_to_date_change_cents)],
          },
          ...(netAssetClass.earlier_years_unclosed_change_cents !== 0
            ? [
                {
                  kind: 'line',
                  key: `na-${netAssetClass.net_asset_class}-earlier`,
                  indent: 2,
                  muted: true,
                  label: 'Change in earlier years, not yet closed',
                  cells: [dashOf(netAssetClass.earlier_years_unclosed_change_cents)],
                } as FaceRow,
              ]
            : []),
        ] as FaceRow[])
      : suspenseOf(netAssetClass.net_asset_class).map(
          (row): FaceRow => ({
            kind: 'detail',
            key: `na-${netAssetClass.net_asset_class}-${row.section}-cue`,
            content: (
              <p
                className="pl-32 font-sans text-s13 tracking-givmo text-text-secondary"
                data-testid="books-net-assets-suspense-cue"
              >
                Includes {words.section(row.section, row.account_type).toLowerCase()} of{' '}
                {money(row.amount_cents)}
              </p>
            ),
          }),
        )),
  ]);

  const makeupToggle = (
    <button
      type="button"
      onClick={() => setMakeup(!makeup)}
      aria-expanded={makeup}
      className="inline-flex items-center gap-2 font-sans text-s12 font-w600 tracking-givmo text-brand-600 hover:underline"
      data-testid="books-net-assets-makeup"
    >
      <Icon icon={makeup ? ChevronDown : ChevronRight} size={12} aria-hidden />
      {makeup ? 'Hide what makes them up' : 'What makes them up'}
    </button>
  );
  const rows: FaceRow[] = [
    { kind: 'heading', label: words.accountType('asset') },
    ...rowsOf('asset').flatMap(lineOf),
    { kind: 'subtotal', label: words.total('assets'), cells: [cellOf(body.total_assets_cents)] },
    { kind: 'spacer' },
    { kind: 'heading', label: words.accountType('liability') },
    ...rowsOf('liability').flatMap(lineOf),
    {
      kind: 'subtotal',
      label: words.total('liabilities'),
      cells: [cellOf(body.total_liabilities_cents)],
    },
    { kind: 'spacer' },
    { kind: 'heading', label: words.accountType('net_asset'), action: makeupToggle },
    ...classRows,
    {
      kind: 'subtotal',
      label: words.total('net_assets'),
      cells: [cellOf(body.total_net_assets_cents)],
    },
    {
      kind: 'total',
      label: words.total('liabilities_and_net_assets'),
      cells: [cellOf(body.total_liabilities_and_net_assets_cents)],
    },
  ];
  return (
    <div className="flex flex-col gap-12">
      <StatementTable
        narrow
        columns={[{ label: '', width: '200px' }]}
        rows={rows}
        testId="books-position-face"
      />
      {!body.balanced ? (
        <Unbalanced testId="books-position-unbalanced">
          These figures don’t balance: {words.total('assets').toLowerCase()} differ from{' '}
          {words.total('liabilities_and_net_assets').toLowerCase()}.
        </Unbalanced>
      ) : null}
    </div>
  );
}

// ── The statement of activities (the board pack's) ───────────────────────────

/** A row's figures in the statement's three columns: each net-asset class, and both. */
function classCells(
  figures: Pick<
    BooksClassColumnTotals,
    'without_donor_restrictions_cents' | 'with_donor_restrictions_cents' | 'total_cents'
  >,
  cell: (cents: number) => ReactNode,
): ReactNode[] {
  return [
    cell(figures.without_donor_restrictions_cents),
    cell(figures.with_donor_restrictions_cents),
    cell(figures.total_cents),
  ];
}

function ActivitiesFace({ body, words }: { body: BooksActivities; words: BooksWords }) {
  const lineOf = (row: BooksActivityRow): FaceRow => ({
    kind: 'line',
    key: `${row.account_type}-${row.section}`,
    indent: 1,
    label: words.section(row.section, row.account_type),
    cells: classCells(row, dashOf),
  });
  const rowsOf = (type: string) => body.rows.filter((row) => row.account_type === type);
  // Every total the face prints is the engine's own (PT-BK22): Books never sums a column.
  const totals = body.activity_column_totals;
  const rows: FaceRow[] = [
    { kind: 'heading', label: words.accountType('revenue') },
    ...rowsOf('revenue').map(lineOf),
    {
      kind: 'subtotal',
      label: words.total('revenue'),
      cells: classCells(totals.total_revenue, cellOf),
    },
    { kind: 'spacer' },
    { kind: 'heading', label: words.accountType('expense') },
    ...rowsOf('expense').map(lineOf),
    {
      kind: 'subtotal',
      label: words.total('expense'),
      cells: classCells(totals.total_expense, cellOf),
    },
    { kind: 'spacer' },
    {
      kind: 'total',
      label: words.total('change_in_net_assets'),
      cells: classCells(totals.change_in_net_assets, cellOf),
    },
  ];
  return (
    <StatementTable
      columns={[
        { label: words.netAssetClass('without_donor_restrictions'), width: '176px' },
        { label: words.netAssetClass('with_donor_restrictions'), width: '176px' },
        { label: 'Total', width: '176px' },
      ]}
      rows={rows}
      testId="books-activities-face"
    />
  );
}

// ── The statement of functional expenses (the board pack's) ──────────────────

function FunctionalExpensesFace({
  body,
  words,
}: {
  body: BooksFunctionalExpenses;
  words: BooksWords;
}) {
  // The matrix's columns are the categories its served total row states, in the pack's order, so a
  // category no account spent on still has its column, and the matrix with no rows still states
  // them (PT-BK22). Each row carries exactly these.
  const totals = body.functional_expense_column_totals;
  const categories = totals.by_category.map((cell) => cell.category);
  const rows: FaceRow[] = [
    ...body.rows.map(
      (row): FaceRow => ({
        kind: 'line',
        key: row.account_code,
        label: row.account_name,
        cells: [
          ...row.by_category.map((cell) => dashOf(cell.amount_cents)),
          dashOf(row.unclassified_cents),
          cellOf(row.total_cents),
        ],
      }),
    ),
    {
      kind: 'total',
      label: words.total('expense'),
      cells: [
        ...totals.by_category.map((cell) => cellOf(cell.amount_cents)),
        cellOf(totals.unclassified_cents),
        cellOf(totals.total_cents),
      ],
    },
  ];
  return (
    <StatementTable
      firstColumnLabel="Expense"
      columns={[
        ...categories.map((category) => ({ label: words.category(category), width: '150px' })),
        { label: words.category('unclassified'), width: '120px' },
        { label: 'Total', width: '150px' },
      ]}
      rows={rows}
      testId="books-functional-face"
    />
  );
}

/** A restricted reserve's disclosure, from its TYPED fields only (D-BM-16 (a-3)). */
function ReserveNotes({ notes, words }: { notes: BooksReserveNote[]; words: BooksWords }) {
  return (
    <div
      className="flex flex-col gap-12 rounded-lg border border-border-subtle bg-surface-sunken px-20 py-16"
      data-testid="books-reserve-notes"
    >
      {notes.map((note) => (
        <div key={note.note} className="flex flex-col gap-10">
          <div className="flex flex-wrap items-baseline justify-between gap-8">
            <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              {note.label ?? words.section(PROCESSOR_RESERVE, 'asset')}
            </h3>
            {note.amount || note.as_of ? (
              <span className="font-sans text-s13 tabular-nums tracking-givmo text-text-secondary">
                {note.amount ? decimalMoney(note.amount) : ''}
                {note.as_of ? ` held at ${dateShort(note.as_of)}` : ''}
              </span>
            ) : null}
          </div>
          {note.presentation ? (
            <NoteField term={words.reserveNoteField('presentation')}>{note.presentation}</NoteField>
          ) : null}
          {note.set_off_rights.length > 0 ? (
            <NoteField term={words.reserveNoteField('set_off_rights')}>
              <ul className="flex list-disc flex-col gap-4 pl-18">
                {note.set_off_rights.map((right) => (
                  <li key={right}>{right}</li>
                ))}
              </ul>
            </NoteField>
          ) : null}
          {note.presented_gross ? (
            <NoteField term={words.reserveNoteField('presented_gross')}>
              {note.presented_gross}
            </NoteField>
          ) : null}
          {note.classification ? (
            <NoteField term={words.reserveNoteField('classification')}>
              {note.classification}
            </NoteField>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function NoteField({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary">
        {term}
      </p>
      <div className="max-w-[760px] font-sans text-s13 leading-[1.5] tracking-givmo text-text">
        {children}
      </div>
    </div>
  );
}

// ── The Form 990 view of the books ───────────────────────────────────────────

function lineLabel(line: string, description: string) {
  return (
    <span>
      <span className="mr-8 inline-block w-[36px] tabular-nums text-text-tertiary">{line}</span>
      {description}
    </span>
  );
}

function Form990Face({ body, words }: { body: BooksForm990View; words: BooksWords }) {
  const columns = Object.keys(body.part_ix_column_totals);
  const revenueRows: FaceRow[] = [
    ...body.part_viii.map(
      (line): FaceRow => ({
        kind: 'line',
        key: `viii-${line.line}`,
        label: lineLabel(line.line, line.description),
        cells: [dashOf(line.total_cents)],
      }),
    ),
    { kind: 'total', label: 'Total revenue', cells: [cellOf(body.total_revenue_cents)] },
  ];
  const expenseRows: FaceRow[] = [
    ...body.part_ix.map(
      (line): FaceRow => ({
        kind: 'line',
        key: `ix-${line.line}`,
        label: lineLabel(line.line, line.description),
        cells: [
          cellOf(line.total_cents),
          ...columns.map((column) => dashOf(line.columns?.[column] ?? 0)),
        ],
      }),
    ),
    {
      kind: 'total',
      label: 'Total functional expenses',
      cells: [
        cellOf(body.total_expense_cents),
        ...columns.map((column) => cellOf(body.part_ix_column_totals[column] ?? 0)),
      ],
    },
  ];
  const parts = body.balance_parts;
  // What the face keeps off is the pack's to say, account by account on the wire: its sections in
  // their served words and order, never a list of sections this page knows.
  const keptOff = parts
    ? [
        ...new Map(
          [
            ...(parts.beginning_of_year.off_face_balances ?? []),
            ...(parts.end_of_year.off_face_balances ?? []),
          ].map((row) => [
            `${row.account_type}/${row.section}`,
            words.section(row.section, row.account_type),
          ]),
        ).values(),
      ]
    : [];
  return (
    <div className="flex flex-col gap-20">
      <p className="px-16 pt-8 font-sans text-s13 tracking-givmo text-text-secondary">
        These books arranged by the lines of Form 990. It is a view of the books, not a return.
      </p>
      <section className="flex flex-col gap-4">
        <h3 className="px-16 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          Part VIII · Revenue
        </h3>
        <StatementTable
          narrow
          columns={[{ label: '', width: '200px' }]}
          rows={revenueRows}
          testId="books-990-viii"
        />
      </section>
      <section className="flex flex-col gap-4">
        <h3 className="px-16 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          Part IX · Functional expenses
        </h3>
        <StatementTable
          firstColumnLabel="Line"
          columns={[
            { label: '(A) Total', width: '140px' },
            ...columns.map((column) => ({ label: words.partIxColumn(column), width: '150px' })),
          ]}
          rows={expenseRows}
          testId="books-990-ix"
        />
      </section>
      <dl className="flex flex-col gap-4 px-16">
        <Residual
          term="Expense not placed in a column"
          value={
            body.unclassified_expense_cents === 0 ? 'None' : money(body.unclassified_expense_cents)
          }
        />
        <Residual
          term="Accounts with activity the map doesn’t place"
          value={body.unmapped_accounts.length === 0 ? 'None' : body.unmapped_accounts.join(', ')}
        />
      </dl>
      {parts ? (
        <>
          <section className="flex flex-col gap-4">
            <h3 className="px-16 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              Part X · Balance sheet totals
            </h3>
            <StatementTable
              columns={[
                {
                  label: `Beginning of year (${periodName(parts.beginning_of_year.as_of_period)})`,
                  width: '200px',
                },
                {
                  label: `End of year (${periodName(parts.end_of_year.as_of_period)})`,
                  width: '200px',
                },
              ]}
              rows={[
                {
                  kind: 'line',
                  label: words.total('assets'),
                  cells: [
                    cellOf(parts.beginning_of_year.total_assets_cents),
                    cellOf(parts.end_of_year.total_assets_cents),
                  ],
                },
                {
                  kind: 'line',
                  label: words.total('liabilities'),
                  cells: [
                    cellOf(parts.beginning_of_year.total_liabilities_cents),
                    cellOf(parts.end_of_year.total_liabilities_cents),
                  ],
                },
                ...Object.keys(parts.end_of_year.net_assets).map(
                  (netAssetClass): FaceRow => ({
                    kind: 'line',
                    key: `x-${netAssetClass}`,
                    label: `${words.accountType('net_asset')} ${words.netAssetClass(netAssetClass).toLowerCase()}`,
                    cells: [
                      cellOf(parts.beginning_of_year.net_assets[netAssetClass] ?? 0),
                      cellOf(parts.end_of_year.net_assets[netAssetClass] ?? 0),
                    ],
                  }),
                ),
                {
                  kind: 'subtotal',
                  label: words.total('net_assets'),
                  cells: [
                    cellOf(parts.beginning_of_year.total_net_assets_cents),
                    cellOf(parts.end_of_year.total_net_assets_cents),
                  ],
                },
                {
                  kind: 'line',
                  label:
                    keptOff.length > 0
                      ? `Balances the form keeps off: ${listWords(keptOff)}`
                      : 'Balances the form keeps off',
                  muted: true,
                  cells: [
                    dashOf(parts.beginning_of_year.difference_to_board_pack_cents),
                    dashOf(parts.end_of_year.difference_to_board_pack_cents),
                  ],
                },
                {
                  kind: 'line',
                  label: 'Net assets on the statement of financial position',
                  muted: true,
                  cells: [
                    cellOf(parts.beginning_of_year.board_pack_net_assets_cents),
                    cellOf(parts.end_of_year.board_pack_net_assets_cents),
                  ],
                },
              ]}
              testId="books-990-x"
            />
          </section>
          <section className="flex flex-col gap-4">
            <h3 className="px-16 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              Part XI · Change in net assets
            </h3>
            <StatementTable
              narrow
              columns={[{ label: '', width: '200px' }]}
              rows={[
                {
                  kind: 'line',
                  label: 'Net assets at the beginning of the year',
                  cells: [cellOf(parts.net_assets_roll_forward.beginning_net_assets_cents)],
                },
                {
                  kind: 'line',
                  label: 'Revenue less expenses',
                  cells: [cellOf(parts.net_assets_roll_forward.revenue_less_expenses_cents)],
                },
                {
                  kind: 'line',
                  label: 'Other changes in net assets',
                  cells: [cellOf(parts.net_assets_roll_forward.other_changes_cents)],
                },
                ...Object.entries(parts.net_assets_roll_forward.other_changes).map(
                  ([name, cents]): FaceRow => ({
                    kind: 'line',
                    key: `xi-${name}`,
                    indent: 2,
                    muted: true,
                    label: words.rollForward(name),
                    cells: [dashOf(cents)],
                  }),
                ),
                {
                  kind: 'total',
                  label: 'Net assets at the end of the year',
                  cells: [cellOf(parts.net_assets_roll_forward.end_net_assets_cents)],
                },
              ]}
              testId="books-990-xi"
            />
            {!parts.net_assets_roll_forward.articulates ? (
              <Unbalanced testId="books-990-unarticulated">
                The change in net assets doesn’t add up to the year-end figure.
              </Unbalanced>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
}

function Residual({ term, value }: { term: string; value: string }) {
  return (
    <div className="flex gap-8 font-sans text-s13 tracking-givmo">
      <dt className="text-text-secondary">{term}:</dt>
      <dd className="text-text">{value}</dd>
    </div>
  );
}

// ── A trial balance (every kind of books) ────────────────────────────────────

function TrialBalanceFace({ body, words }: { body: BooksTrialBalance; words: BooksWords }) {
  const [showMembers, setShowMembers] = useState(false);
  const [showZero, setShowZero] = useState(false);
  const idle = body.rows.filter((row) => row.debit_cents === 0 && row.credit_cents === 0).length;
  const rows: FaceRow[] = [
    ...body.rows
      .filter((row) => showZero || row.debit_cents !== 0 || row.credit_cents !== 0)
      .map(
        (row): FaceRow => ({
          kind: 'line',
          key: row.account_code,
          label: (
            <span>
              <span className="mr-12 inline-block w-[40px] tabular-nums text-text-tertiary">
                {row.account_code}
              </span>
              {row.account_name}
            </span>
          ),
          muted: row.debit_cents === 0 && row.credit_cents === 0,
          cells: [dashOf(row.debit_cents), dashOf(row.credit_cents), dashOf(row.net_cents)],
        }),
      ),
    {
      kind: 'total',
      label: 'Totals',
      cells: [cellOf(body.total_debit_cents), cellOf(body.total_credit_cents), ''],
    },
  ];
  const axes = words.axisOrder.filter((axis) =>
    body.dimension_members.some((member) => member.axis_id === axis),
  );
  const unnamedAxes = [
    ...new Set(
      body.dimension_members
        .map((member) => member.axis_id)
        .filter((axis) => !words.axisOrder.includes(axis)),
    ),
  ];
  const breakdown = listWords(
    [...axes, ...unnamedAxes].map((axis) => words.axis(axis).toLowerCase()),
  );
  return (
    <div className="flex flex-col gap-12">
      <ZeroToggle
        what="no activity"
        hidden={idle}
        shown={showZero}
        onToggle={() => setShowZero(!showZero)}
      />
      <StatementTable
        firstColumnLabel="Account"
        columns={[
          { label: 'Debits', width: '160px' },
          { label: 'Credits', width: '160px' },
          { label: 'Net', width: '160px' },
        ]}
        rows={rows}
        testId="books-trial-balance-face"
      />
      <div className="flex flex-wrap items-center justify-between gap-12 px-16">
        <p className="font-sans text-s13 tracking-givmo text-text-secondary">
          {body.balanced ? 'Debits equal credits.' : 'Debits and credits differ.'} Folded from{' '}
          {count(body.entries_folded)} {body.entries_folded === 1 ? 'entry' : 'entries'}.
        </p>
        {body.dimension_members.length > 0 ? (
          <button
            type="button"
            onClick={() => setShowMembers(!showMembers)}
            aria-expanded={showMembers}
            className="inline-flex items-center gap-4 font-sans text-s13 font-w600 tracking-givmo text-brand-600 hover:underline"
            data-testid="books-trial-balance-members"
          >
            <Icon icon={showMembers ? ChevronDown : ChevronRight} size={13} aria-hidden />
            By {breakdown}
          </button>
        ) : null}
      </div>
      {showMembers ? (
        <StatementTable
          firstColumnLabel="Member"
          columns={[
            { label: 'Debits', width: '150px' },
            { label: 'Credits', width: '150px' },
            { label: 'Lines', width: '90px' },
          ]}
          rows={body.dimension_members.map(
            (member: BooksDimensionMember): FaceRow => ({
              kind: 'line',
              key: `${member.axis_id}-${member.member}`,
              label: (
                <span>
                  <span className="mr-8 text-text-tertiary">{words.axis(member.axis_id)}</span>
                  {memberWords(words, member.axis_id, member.member)}
                </span>
              ),
              cells: [
                dashOf(member.debit_cents),
                dashOf(member.credit_cents),
                count(member.line_count),
              ],
            }),
          )}
          testId="books-trial-balance-members-table"
        />
      ) : null}
    </div>
  );
}

// ── Statements of sections of accounts, each with its served subtotal ─────────

function zeroLines(sections: BooksStatementSection[]): number {
  return sections
    .filter((section) => section.lines.length > 1)
    .flatMap((section) => section.lines)
    .filter((line) => line.amount_cents === 0).length;
}

function sectionRows(
  sections: BooksStatementSection[],
  type: string,
  words: BooksWords,
  showZero: boolean,
): FaceRow[] {
  return sections.flatMap((section): FaceRow[] => {
    const label = words.section(section.section, type);
    const lines = showZero
      ? section.lines
      : section.lines.filter((line) => line.amount_cents !== 0);
    // A section of one account, or one whose every account is at zero, reads as ONE row carrying
    // its served subtotal: a heading, a line and a total saying the same figure is noise.
    if (section.lines.length === 1 || lines.length === 0) {
      return [
        {
          kind: 'line',
          key: `${type}-${section.section}`,
          label: <span className="font-w500 text-text">{label}</span>,
          cells: [dashOf(section.subtotal_cents)],
        },
      ];
    }
    return [
      {
        kind: 'line',
        key: `${type}-${section.section}-h`,
        label: <span className="font-w600 text-text-strong">{label}</span>,
        cells: [''],
      },
      ...lines.map(
        (line): FaceRow => ({
          kind: 'line',
          key: `${type}-${line.account_code}`,
          indent: 1,
          muted: line.amount_cents === 0,
          label: line.account_name,
          cells: [dashOf(line.amount_cents)],
        }),
      ),
      {
        kind: 'line',
        key: `${type}-${section.section}-t`,
        indent: 1,
        label: <span className="font-w500">Total {label.toLowerCase()}</span>,
        cells: [
          <span key="subtotal" className="font-w500">
            {money(section.subtotal_cents)}
          </span>,
        ],
      },
    ];
  });
}

function IncomeStatementFace({ body, words }: { body: BooksIncomeStatement; words: BooksWords }) {
  const [showZero, setShowZero] = useState(false);
  const rows: FaceRow[] = [
    { kind: 'heading', label: words.accountType('revenue') },
    ...sectionRows(body.revenue, 'revenue', words, showZero),
    { kind: 'subtotal', label: words.total('revenue'), cells: [cellOf(body.total_revenue_cents)] },
    { kind: 'spacer' },
    { kind: 'heading', label: words.accountType('expense') },
    ...sectionRows(body.expense, 'expense', words, showZero),
    { kind: 'subtotal', label: words.total('expense'), cells: [cellOf(body.total_expense_cents)] },
    { kind: 'spacer' },
    // One label whatever the sign: the served figure carries it.
    { kind: 'total', label: words.total('net_income'), cells: [cellOf(body.net_income_cents)] },
  ];
  return (
    <div className="flex flex-col">
      <ZeroToggle
        narrow
        what="no activity"
        hidden={zeroLines([...body.revenue, ...body.expense])}
        shown={showZero}
        onToggle={() => setShowZero(!showZero)}
      />
      <StatementTable
        narrow
        columns={[{ label: '', width: '200px' }]}
        rows={rows}
        testId="books-income-statement-face"
      />
    </div>
  );
}

function BalanceSheetFace({ body, words }: { body: BooksBalanceSheet; words: BooksWords }) {
  const [showZero, setShowZero] = useState(false);
  const asOf = body.as_of_period != null;
  const rows: FaceRow[] = [
    { kind: 'heading', label: words.accountType('asset') },
    ...sectionRows(body.assets, 'asset', words, showZero),
    { kind: 'subtotal', label: words.total('assets'), cells: [cellOf(body.total_assets_cents)] },
    { kind: 'spacer' },
    { kind: 'heading', label: words.accountType('liability') },
    ...sectionRows(body.liabilities, 'liability', words, showZero),
    {
      kind: 'subtotal',
      label: words.total('liabilities'),
      cells: [cellOf(body.total_liabilities_cents)],
    },
    { kind: 'spacer' },
    { kind: 'heading', label: words.accountType('equity') },
    ...sectionRows(body.equity, 'equity', words, showZero),
    { kind: 'subtotal', label: words.total('equity'), cells: [cellOf(body.total_equity_cents)] },
    {
      kind: 'line',
      key: 'earnings-not-closed',
      label: (
        <span className="font-w500 text-text-strong">{words.total('earnings_not_closed')}</span>
      ),
      cells: [cellOf(body.earnings_not_closed_cents)],
    },
    ...(asOf
      ? ([
          {
            kind: 'line',
            key: 'earnings-fiscal-year-to-date',
            indent: 1,
            muted: true,
            label:
              body.fiscal_year != null
                ? `Fiscal year ${body.fiscal_year} to date`
                : words.scope('year_to_date'),
            cells: [dashOf(body.fiscal_year_to_date_earnings_cents ?? 0)],
          },
          {
            kind: 'line',
            key: 'earnings-earlier-years',
            indent: 1,
            muted: true,
            label: 'Earlier years',
            cells: [dashOf(body.prior_years_earnings_not_closed_cents ?? 0)],
          },
        ] as FaceRow[])
      : []),
    {
      kind: 'total',
      label: words.total('liabilities_and_equity'),
      cells: [cellOf(body.total_liabilities_and_equity_cents)],
    },
  ];
  return (
    <div className="flex flex-col gap-12">
      <ZeroToggle
        narrow
        what="no balance"
        hidden={zeroLines([...body.assets, ...body.liabilities, ...body.equity])}
        shown={showZero}
        onToggle={() => setShowZero(!showZero)}
      />
      <StatementTable
        narrow
        columns={[{ label: '', width: '200px' }]}
        rows={rows}
        testId="books-balance-sheet-face"
      />
      {!body.balanced ? (
        <Unbalanced testId="books-balance-sheet-unbalanced">
          These figures don’t balance: {words.total('assets').toLowerCase()} differ from{' '}
          {words.total('liabilities_and_equity').toLowerCase()}.
        </Unbalanced>
      ) : null}
    </div>
  );
}
